import React from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import {
  Close,
  HeadsetRounded,
  InfoRounded,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import {
  MediaMuteButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from "media-chrome/react";

const MobileMaximizedUI = ({
  watchType,
  showEpisodes,
  setShowEpisodes,
  handleMinimize,
  handleClosePlayer,
  dispatch,
  setWatchType,
  setIsMuted,
  setVolume,
  playerRef,
  isMobile,
  isPlaying,
}) => {
  const theme = useTheme();
  const [controlsVisible, setControlsVisible] = React.useState(true);
  const timerRef = React.useRef(null);

  const resetTimer = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (isPlaying) {
      timerRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  }, [isPlaying]);

  React.useEffect(() => {
    if (controlsVisible && isPlaying) {
      resetTimer();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [controlsVisible, isPlaying, resetTimer]);

  const handleBackgroundClick = () => {
    setControlsVisible((prev) => !prev);
  };

  const onControlInteraction = () => {
    resetTimer();
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1,
      }}
      onClick={handleBackgroundClick}
    >
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          right: "10px",
          display: "flex",
          flexDirection: "row",
          gap: 2,
          zIndex: 10,
          opacity: controlsVisible ? 0.8 : 0,
          pointerEvents: controlsVisible ? "auto" : "none",
          transition: "opacity 0.3s",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title={"Passer en mode écoute"} placement="bottom">
          <IconButton
            onClick={() => {
              dispatch(setWatchType("listen"));
            }}
            sx={{
              color: "white",
              backgroundColor: (theme) => theme.palette.videoColors.button,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.videoColors.buttonHover,
              },
            }}
          >
            <HeadsetRounded />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Voir la liste des épisodes"} placement="bottom">
          <IconButton
            onClick={() => {
              setShowEpisodes(!showEpisodes);
            }}
            sx={{
              color: "white",
              backgroundColor: (theme) => theme.palette.videoColors.button,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.videoColors.buttonHover,
              },
            }}
          >
            <InfoRounded color={showEpisodes ? "primary" : undefined} />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Minimiser la vidéo"} placement="bottom">
          <IconButton
            onClick={handleMinimize}
            sx={{
              color: "white",
              backgroundColor: (theme) => theme.palette.videoColors.button,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.videoColors.buttonHover,
              },
            }}
          >
            <KeyboardArrowDownRounded />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Fermer"} placement="bottom">
          <IconButton
            onClick={handleClosePlayer}
            sx={{
              color: "white",
              backgroundColor: (theme) => theme.palette.videoColors.button,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.videoColors.buttonHover,
              },
            }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "row",
          gap: 4,
          zIndex: 5,
          opacity: controlsVisible ? 0.8 : 0,
          transition: "opacity 0.3s",
          pointerEvents: controlsVisible ? "auto" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={onControlInteraction}
        onTouchStart={onControlInteraction}
      >
        <MediaSeekBackwardButton
          notooltip
          seekOffset={5}
          style={{
            transform: "scale(1.1)",
            borderRadius: "50%",
            backgroundColor: theme.palette.videoColors.button,
            pointerEvents: controlsVisible ? "auto" : "none",
          }}
        />
        <MediaPlayButton
          notooltip
          style={{
            transform: "scale(1.5)",
            borderRadius: "50%",
            backgroundColor: theme.palette.videoColors.button,
            pointerEvents: controlsVisible ? "auto" : "none",
          }}
        />
        <MediaSeekForwardButton
          notooltip
          seekOffset={5}
          style={{
            transform: "scale(1.1)",
            borderRadius: "50%",
            backgroundColor: theme.palette.videoColors.button,
            pointerEvents: controlsVisible ? "auto" : "none",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          zIndex: 5,
          opacity: controlsVisible ? 0.8 : 0,
          transition: "opacity 0.3s",
          pointerEvents: controlsVisible ? "auto" : "none",
          background: theme.palette.videoColors.button,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={onControlInteraction}
        onTouchStart={onControlInteraction}
      >
        <MediaMuteButton
          onClick={() => {
            setIsMuted((prev) => !prev);
          }}
        />
        <MediaTimeDisplay showDuration style={{ color: "white" }} />
        <MediaTimeRange
          style={{
            flexGrow: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export default MobileMaximizedUI;
